"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { getDashboardPathForRole } from "@/lib/authRedirects";

interface GuestRouteGuardProps {
  children: React.ReactNode;
  /** When true, keep children mounted through an in-progress form login (button UX owns the wait). */
  isSubmitting?: boolean;
}

function AuthLoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aralin-green"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

export default function GuestRouteGuard({
  children,
  isSubmitting = false,
}: GuestRouteGuardProps) {
  const { user, isLoading, isAuthenticated, checkAndShowTermsModal } = useAuth();
  const router = useRouter();
  // After the first auth check finishes, ignore isLoading so login() submit
  // (which also sets AuthContext isLoading) does not replace the form with a full-page spinner.
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasHydrated(true);
    }
  }, [isLoading]);

  useEffect(() => {
    // During form submit, login/page owns redirect via loginSuccess — avoid racing here.
    if (isSubmitting) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      return;
    }

    if (user.has_accepted_terms_and_conditions !== true) {
      checkAndShowTermsModal();
      return;
    }

    router.replace(getDashboardPathForRole(user.role));
  }, [isSubmitting, isLoading, isAuthenticated, user, router, checkAndShowTermsModal]);

  // Initial session check only — not form submission
  if (isLoading && !hasHydrated) {
    return <AuthLoadingSpinner />;
  }

  // Fully authenticated prior session about to redirect — hide form.
  // Keep children when terms are pending (modal in root layout overlays the form)
  // or while isSubmitting (button UX owns the wait).
  if (
    isAuthenticated &&
    user &&
    user.has_accepted_terms_and_conditions === true &&
    !isSubmitting
  ) {
    return <AuthLoadingSpinner />;
  }

  return <>{children}</>;
}
